import type {
  AnnualIncome,
  AssetCategory,
  AssetItem,
  AssetsData,
  Bill,
  BillFrequency,
  BillsData,
  BudgetCategory,
  BudgetItem,
  BudgetSummary,
  CategoryRow,
  ClaimAge,
  DashboardData,
  DataSource,
  DebtCategory,
  DebtItem,
  DebtsData,
  Funding,
  Gift,
  GiftsData,
  IncomeByJobData,
  IncomeSource,
  Investment,
  InvestmentCategory,
  InvestmentsData,
  JobRecord,
  Link,
  RetirementAccount,
  RetirementProjection,
  RetirementYear,
  SocialSecurityData,
  Tri,
} from './types';

export interface SheetTabConfig {
  spending: string;
  cashIn: string;
  netWorth: string;
  incomeHistory: string;
  incomeByJob: string;
  cashOut: string;
  assets: string;
  stocksCrypto: string;
  debts: string;
  links: string;
  bills: string;
  gifts: string;
  retirementDakota: string;
  retirementNatalia: string;
  socialSecurity: string;
}

export interface SheetDataSourceConfig {
  endpoint: string;
  tabs: SheetTabConfig;
  getToken: () => string | null;
  onAuthError?: () => void;
}

interface SheetResponse {
  values?: string[][];
  hyperlinks?: string[][];
}

function parseAmount(raw: string | undefined): number {
  if (!raw) return 0;
  const cleaned = raw.replace(/[$,\s]/g, '');
  if (cleaned === '' || cleaned === '-') return 0;
  const negative = /^\(.*\)$/.test(cleaned);
  const numeric = parseFloat(cleaned.replace(/[()]/g, ''));
  if (Number.isNaN(numeric)) return 0;
  return negative ? -numeric : numeric;
}

function parsePercent(raw: string | undefined): number {
  if (!raw) return 0;
  return parseFloat(raw.replace('%', '')) || 0;
}

function findRowByLabel(rows: string[][], label: string): string[] | undefined {
  const target = label.toLowerCase().trim();
  return rows.find((r) => (r[0] ?? '').toLowerCase().trim() === target);
}

interface TriColumns {
  household: number;
  dakota: number;
  natalia: number;
}

// Aliases let older spreadsheet revisions (e.g. a column once named "Joint"
// before being renamed to "Household") still resolve to the right Tri member.
const TRI_HEADER_ALIASES: Record<keyof TriColumns, string[]> = {
  household: ['household', 'joint', 'combined'],
  dakota: ['dakota'],
  natalia: ['natalia'],
};

// Locate the column index for each Tri member by header text. We scan the
// first few rows because some tabs carry a banner in row 0 and the actual
// column headers on row 1. Returns -1 for any header that isn't found.
function findTriColumns(values: string[][]): TriColumns {
  const matchIn = (row: string[] | undefined, aliases: string[]): number => {
    if (!row) return -1;
    for (let i = 0; i < row.length; i++) {
      const cell = (row[i] ?? '').toLowerCase().trim();
      if (aliases.includes(cell)) return i;
    }
    return -1;
  };
  for (const row of values.slice(0, 3)) {
    const household = matchIn(row, TRI_HEADER_ALIASES.household);
    const dakota = matchIn(row, TRI_HEADER_ALIASES.dakota);
    const natalia = matchIn(row, TRI_HEADER_ALIASES.natalia);
    if (household >= 0 && dakota >= 0 && natalia >= 0) {
      return { household, dakota, natalia };
    }
  }
  return { household: -1, dakota: -1, natalia: -1 };
}

function parseSpending(values: string[][]): DashboardData['spending'] {
  // Columns in the Spending tab:
  //   0: name | 1: combined % | 2: household $ | 3: dakota %
  //   4: dakota $ | 5: natalia % | 6: natalia $
  // Last data row is "Total Monthly Spending" (the totals row).
  const dataRows = values
    .slice(1)
    .filter((r) => (r[0] ?? '').trim() !== '');

  const totalsRow = dataRows.find((r) =>
    (r[0] ?? '').toLowerCase().includes('total monthly spending')
  );

  const categoryRows: CategoryRow[] = dataRows
    .filter((r) => !(r[0] ?? '').toLowerCase().includes('total monthly spending'))
    .map((r) => ({
      name: r[0].trim(),
      combinedPercent: parsePercent(r[1]),
      household: parseAmount(r[2]),
      dakota: parseAmount(r[4]),
      natalia: parseAmount(r[6]),
    }));

  const totals: Tri = totalsRow
    ? {
        household: parseAmount(totalsRow[2]),
        dakota: parseAmount(totalsRow[4]),
        natalia: parseAmount(totalsRow[6]),
      }
    : categoryRows.reduce<Tri>(
        (acc, c) => ({
          household: acc.household + c.household,
          dakota: acc.dakota + c.dakota,
          natalia: acc.natalia + c.natalia,
        }),
        { household: 0, dakota: 0, natalia: 0 }
      );

  return { byCategory: categoryRows, totals };
}

function parseCashIn(values: string[][]): DashboardData['income'] {
  // Columns: 0 name, 1 household, 2 dakota, 3 natalia.
  const triFrom = (row: string[] | undefined): Tri => ({
    household: parseAmount(row?.[1]),
    dakota: parseAmount(row?.[2]),
    natalia: parseAmount(row?.[3]),
  });
  // Tax rows are stored parenthesized (negative) in the sheet — flip to absolute
  // so callers display them as paid amounts without each consumer doing it.
  const triAbs = (row: string[] | undefined): Tri => {
    const t = triFrom(row);
    return {
      household: Math.abs(t.household),
      dakota: Math.abs(t.dakota),
      natalia: Math.abs(t.natalia),
    };
  };

  // Income sources are the rows between the "Monthly Income" sub-header and
  // the "Total Monthly Income (Gross)" totals row.
  const monthlyIdx = values.findIndex(
    (r) => (r[0] ?? '').toLowerCase().trim() === 'monthly income'
  );
  const grossMonthlyIdx = values.findIndex(
    (r) => (r[0] ?? '').toLowerCase().trim() === 'total monthly income (gross)'
  );
  // Resolve the per-source "Taxes" column from whichever header row carries
  // the Tri labels (same row Household/Dakota/Natalia sit on). Falls back to
  // -1 when the column is absent — older sheet revisions don't have it.
  const taxColIdx = (() => {
    for (const row of values.slice(0, 3)) {
      for (let i = 0; i < row.length; i++) {
        if ((row[i] ?? '').toLowerCase().trim() === 'taxes') return i;
      }
    }
    return -1;
  })();
  const sources: IncomeSource[] =
    monthlyIdx >= 0 && grossMonthlyIdx > monthlyIdx
      ? values
          .slice(monthlyIdx + 1, grossMonthlyIdx)
          .filter((r) => (r[0] ?? '').trim() !== '')
          .map((r) => ({
            name: r[0].trim(),
            ...triFrom(r),
            monthlyTaxes: taxColIdx >= 0 ? Math.abs(parseAmount(r[taxColIdx])) : 0,
          }))
      : [];

  return {
    netMonthly: triFrom(findRowByLabel(values, 'Total Monthly Income (Net)')),
    grossMonthly: triFrom(findRowByLabel(values, 'Total Monthly Income (Gross)')),
    netAnnual: triFrom(findRowByLabel(values, 'Total Annual Income (Net)')),
    grossAnnual: triFrom(findRowByLabel(values, 'Total Annual Income (Gross)')),
    remaining: triFrom(findRowByLabel(values, 'Remaining Monthly After Expenditures & Savings')),
    sources,
    taxes: (() => {
      // Sheet rows for the individual tax types are MONTHLY values (in the
      // "Monthly Taxes" section), but `Total Annual Taxes` and every other
      // figure on the income page is annual. Multiply by 12 here so all
      // four tax fields are on the same annual scale — consumers can take
      // the breakdown as-is against grossAnnual / netAnnual without having
      // to know about the per-row scale mismatch.
      const annualize = (t: Tri): Tri => ({
        household: t.household * 12,
        dakota: t.dakota * 12,
        natalia: t.natalia * 12,
      });
      return {
        state: annualize(triAbs(findRowByLabel(values, 'State Taxes'))),
        federal: annualize(
          triAbs(findRowByLabel(values, 'Federal Taxes (Variable Tax Rates)')),
        ),
        fica: annualize(
          triAbs(findRowByLabel(values, 'FICA Taxes (Variable Tax Rates)')),
        ),
        total: triAbs(findRowByLabel(values, 'Total Annual Taxes')),
      };
    })(),
  };
}

export function parseNetWorth(values: string[][]): DashboardData['netWorth'] {
  // Sections separated by empty rows. The first non-empty row of each
  // section is the totals row for that section, followed by breakdown rows.
  // Section order: Assets, Liabilities, then a final summary row labeled
  // "Total Assets" (which is actually net worth = assets - liabilities).
  //
  // Columns are resolved from the header row so historical revisions with a
  // different column order (e.g. Dakota/Natalia swapped, or a Joint→Household
  // rename) still parse. Falls back to the current layout (1/2/3) when no
  // header row matches — keeps live sheets working even if a header gets typo'd.
  const detected = findTriColumns(values);
  const cols: TriColumns =
    detected.household >= 0 && detected.dakota >= 0 && detected.natalia >= 0
      ? detected
      : { household: 1, dakota: 2, natalia: 3 };
  const triFrom = (row: string[] | undefined): Tri => ({
    household: parseAmount(row?.[cols.household]),
    dakota: parseAmount(row?.[cols.dakota]),
    natalia: parseAmount(row?.[cols.natalia]),
  });
  const rowToCategory = (r: string[]): CategoryRow => ({
    name: (r[0] ?? '').trim(),
    household: parseAmount(r[cols.household]),
    dakota: parseAmount(r[cols.dakota]),
    natalia: parseAmount(r[cols.natalia]),
  });

  // Group rows into sections by empty-row separators
  const sections: string[][][] = [];
  let current: string[][] = [];
  for (const r of values.slice(1)) {
    if ((r[0] ?? '').trim() === '') {
      if (current.length) sections.push(current);
      current = [];
    } else {
      current.push(r);
    }
  }
  if (current.length) sections.push(current);

  const findSection = (name: string) =>
    sections.find((s) => (s[0]?.[0] ?? '').toLowerCase().trim() === name.toLowerCase());

  const assetSection = findSection('Assets');
  const liabilitySection = findSection('Liabilities');
  const netWorthRow = findRowByLabel(values, 'Total Assets');

  const assetsTotal = triFrom(assetSection?.[0]);
  const liabilitiesTotal = triFrom(liabilitySection?.[0]);
  const assets = (assetSection?.slice(1) ?? []).map(rowToCategory);
  const liabilities = (liabilitySection?.slice(1) ?? []).map(rowToCategory);

  const computed: Tri = {
    household: assetsTotal.household - liabilitiesTotal.household,
    dakota: assetsTotal.dakota - liabilitiesTotal.dakota,
    natalia: assetsTotal.natalia - liabilitiesTotal.natalia,
  };
  const netWorth = netWorthRow ? triFrom(netWorthRow) : computed;

  return { assetsTotal, liabilitiesTotal, netWorth, assets, liabilities };
}

// Maps a cell value from the Cash Out tab's "Account" column to a funding
// bucket. The sheet uses one of: Shared, Personal, Vault, Business, Auto.
// Anything blank or unrecognized falls back to personal.
function classifyFunding(raw: string | undefined): Funding {
  const v = (raw ?? '').toLowerCase().trim();
  if (v === 'shared') return 'shared';
  if (v === 'vault') return 'vault';
  if (v === 'business') return 'business';
  if (v === 'auto') return 'auto';
  return 'personal';
}

function parseCashOut(values: string[][]): DashboardData['budget'] {
  // Tab columns: 0 name, 1 household, 2 dakota, 3 natalia, then a trailing
  // "Account" column (Shared / Personal / Vault / Business / Auto) whose
  // exact index is resolved from the header row below.
  // Layout: a banner header row, blank, then category sections separated by
  // blank rows. Each category section starts with a header row holding the
  // category total, followed by line-item rows. The final three sections are
  // summary blocks ("Monthly Expenses", "Monthly Savings…", "Monthly Outgoing")
  // each with a Monthly + Annual row pair.
  const tri = (row: string[] | undefined): Tri => ({
    household: parseAmount(row?.[1]),
    dakota: parseAmount(row?.[2]),
    natalia: parseAmount(row?.[3]),
  });

  // Locate the "Account" column from whichever of the first few rows carries
  // the header labels. The tab has historically been 4 columns (name + Tri);
  // anything beyond column 3 is the new Account column. Falls back to -1 for
  // sheets that haven't been migrated yet — those items default to personal.
  const accountColIdx = (() => {
    for (const row of values.slice(0, 3)) {
      for (let i = 4; i < row.length; i++) {
        if ((row[i] ?? '').toLowerCase().trim() === 'account') return i;
      }
    }
    return -1;
  })();

  type IndexedRow = { values: string[]; index: number };
  const sections: IndexedRow[][] = [];
  let current: IndexedRow[] = [];
  // Skip the very first row (the banner "Cash Out | Household | Dakota | Natalia | Account").
  for (let i = 1; i < values.length; i++) {
    const r = values[i];
    if ((r[0] ?? '').trim() === '') {
      if (current.length) sections.push(current);
      current = [];
    } else {
      current.push({ values: r, index: i });
    }
  }
  if (current.length) sections.push(current);

  // The savings summary row historically read "Monthly Savings, Retirement,
  // & Investments"; after the category rename it may read "Monthly Savings
  // & Investments", "Savings & Investments", "Savings & Investing", etc.
  // Match any label that begins with "monthly savings" OR "savings" so the
  // parser stays robust through sheet edits during/after the merge.
  const SUMMARY_NAMES = new Set(['monthly expenses', 'monthly outgoing']);
  const isSavingsSummary = (name: string): boolean =>
    name.startsWith('monthly savings') || name.startsWith('savings');

  const categories: BudgetCategory[] = [];
  const summary: BudgetSummary = {
    monthlyExpenses: { household: 0, dakota: 0, natalia: 0 },
    monthlySavings: { household: 0, dakota: 0, natalia: 0 },
    monthlyOutgoing: { household: 0, dakota: 0, natalia: 0 },
    annualExpenses: { household: 0, dakota: 0, natalia: 0 },
    annualSavings: { household: 0, dakota: 0, natalia: 0 },
    annualOutgoing: { household: 0, dakota: 0, natalia: 0 },
  };

  for (const section of sections) {
    const head = section[0];
    const headName = (head?.values[0] ?? '').toLowerCase().trim();

    if (SUMMARY_NAMES.has(headName) || isSavingsSummary(headName)) {
      const monthly = tri(section[0]?.values);
      const annual = tri(section[1]?.values);
      if (headName === 'monthly expenses') {
        summary.monthlyExpenses = monthly;
        summary.annualExpenses = annual;
      } else if (isSavingsSummary(headName)) {
        summary.monthlySavings = monthly;
        summary.annualSavings = annual;
      } else if (headName === 'monthly outgoing') {
        summary.monthlyOutgoing = monthly;
        summary.annualOutgoing = annual;
      }
      continue;
    }

    const items: BudgetItem[] = section.slice(1).map((r) => {
      const funding = classifyFunding(
        accountColIdx >= 0 ? r.values[accountColIdx] : undefined
      );
      return {
        name: (r.values[0] ?? '').trim(),
        ...tri(r.values),
        funding,
        autoActual: funding === 'auto',
      };
    });
    // Prefer the header row's totals (they're typically a sheet formula
    // that may include rounding the items don't), but if the header is
    // blank — common for sections at the bottom of the tab where the
    // sheet doesn't bother computing a category total — fall back to
    // summing items so the category doesn't quietly show $0.
    const headerTotal = tri(head?.values);
    const headerHasValue =
      headerTotal.household !== 0 ||
      headerTotal.dakota !== 0 ||
      headerTotal.natalia !== 0;
    const total: Tri = headerHasValue
      ? headerTotal
      : items.reduce<Tri>(
          (acc, i) => ({
            household: acc.household + i.household,
            dakota: acc.dakota + i.dakota,
            natalia: acc.natalia + i.natalia,
          }),
          { household: 0, dakota: 0, natalia: 0 },
        );
    categories.push({
      name: (head?.values[0] ?? '').trim(),
      ...total,
      items,
    });
  }

  return { categories, summary };
}

function parseAssets(values: string[][]): AssetsData {
  // Columns:
  //   0: name
  //   1: location (institution / platform)
  //   2: annual interest rate (string %)
  //   3: monthly interest rate (string %)
  //   4: annual compound (household)   5: monthly compound (household)   6: balance (household)
  //   7: annual compound (dakota)      8: monthly compound (dakota)      9: balance (dakota)
  //  10: annual compound (natalia)    11: monthly compound (natalia)    12: balance (natalia)
  // Layout: a banner header row, blank, then category sections separated by
  // blank rows. Each section starts with a header row containing the category
  // totals, followed by item rows. Final section is "Total Assets".
  const triBalance = (row: string[] | undefined): Tri => ({
    household: parseAmount(row?.[6]),
    dakota: parseAmount(row?.[9]),
    natalia: parseAmount(row?.[12]),
  });
  const triAnnual = (row: string[] | undefined): Tri => ({
    household: parseAmount(row?.[4]),
    dakota: parseAmount(row?.[7]),
    natalia: parseAmount(row?.[10]),
  });
  const triMonthly = (row: string[] | undefined): Tri => ({
    household: parseAmount(row?.[5]),
    dakota: parseAmount(row?.[8]),
    natalia: parseAmount(row?.[11]),
  });

  const sections: string[][][] = [];
  let current: string[][] = [];
  // Skip the banner row.
  for (const r of values.slice(1)) {
    if ((r[0] ?? '').trim() === '') {
      if (current.length) sections.push(current);
      current = [];
    } else {
      current.push(r);
    }
  }
  if (current.length) sections.push(current);

  const categories: AssetCategory[] = [];
  let totals: AssetsData['totals'] = {
    balance: { household: 0, dakota: 0, natalia: 0 },
    annualEarnings: { household: 0, dakota: 0, natalia: 0 },
    monthlyEarnings: { household: 0, dakota: 0, natalia: 0 },
  };

  for (const section of sections) {
    const head = section[0];
    const headName = (head?.[0] ?? '').trim();

    if (headName.toLowerCase() === 'total assets') {
      totals = {
        balance: triBalance(head),
        annualEarnings: triAnnual(head),
        monthlyEarnings: triMonthly(head),
      };
      continue;
    }

    const items: AssetItem[] = section.slice(1).map((r) => ({
      name: (r[0] ?? '').trim(),
      location: (r[1] ?? '').trim(),
      balance: triBalance(r),
      annualRate: parsePercent(r[2]) / 100,
      monthlyRate: parsePercent(r[3]) / 100,
      annualEarnings: triAnnual(r),
      monthlyEarnings: triMonthly(r),
    }));

    categories.push({
      name: headName,
      balance: triBalance(head),
      annualEarnings: triAnnual(head),
      monthlyEarnings: triMonthly(head),
      items,
    });
  }

  return { categories, totals };
}

function parseStocksCrypto(values: string[][]): InvestmentsData {
  // Columns:
  //   0: ticker, 1: name, 2: location, 3: liquidity, 4: unit value
  //   5: household value, 6: position, 7: invested (household), 8: profit (household), 9: change %, 10: portfolio %
  //  11: dakota value, 12: dakota position, 13: dakota invested, 14: dakota profit, 15: dakota change %, 16: dakota portfolio %
  //  17: natalia value
  // Layout: banner row, blank, then category sections (Index Funds, Public
  // Stocks, Private Stocks, Cryptocurrency) separated by blank rows. The final
  // section is "Total" with the grand totals.
  const triValue = (row: string[] | undefined): Tri => ({
    household: parseAmount(row?.[5]),
    dakota: parseAmount(row?.[11]),
    natalia: parseAmount(row?.[17]),
  });
  // Position uses parseFloat directly since values like "0.21838" or "5000" are
  // share/coin counts, not currency.
  const parseFloatSafe = (raw: string | undefined): number => {
    if (!raw) return 0;
    const n = parseFloat(raw.toString().replace(/[$,\s]/g, ''));
    return Number.isNaN(n) ? 0 : n;
  };
  // The spreadsheet's columns 12-16 sit under a "Dakota" header, but the actual
  // values are NATALIA's detail (verifiable on the BTC row: col 12 = 0.00132871
  // BTC and col 14 = $108.10, both Natalia's). The header is mislabeled.
  // We treat these columns as Natalia's data and derive Dakota by subtraction.
  const triPosition = (row: string[] | undefined): Tri => {
    const household = parseFloatSafe(row?.[6]);
    const natalia = parseFloatSafe(row?.[12]);
    return { household, dakota: household - natalia, natalia };
  };
  const triInvested = (row: string[] | undefined): Tri => {
    const household = parseAmount(row?.[7]);
    const natalia = parseAmount(row?.[13]);
    return { household, dakota: household - natalia, natalia };
  };
  const triProfit = (row: string[] | undefined): Tri => {
    const household = parseAmount(row?.[8]);
    const natalia = parseAmount(row?.[14]);
    return { household, dakota: household - natalia, natalia };
  };

  const sections: string[][][] = [];
  let current: string[][] = [];
  for (const r of values.slice(1)) {
    if ((r[0] ?? '').trim() === '') {
      if (current.length) sections.push(current);
      current = [];
    } else {
      current.push(r);
    }
  }
  if (current.length) sections.push(current);

  const categories: InvestmentCategory[] = [];
  let totals: InvestmentsData['totals'] = {
    value: { household: 0, dakota: 0, natalia: 0 },
    invested: { household: 0, dakota: 0, natalia: 0 },
    profit: { household: 0, dakota: 0, natalia: 0 },
  };

  for (const section of sections) {
    const head = section[0];
    const headName = (head?.[0] ?? '').trim();

    if (headName.toLowerCase() === 'total') {
      totals = {
        value: triValue(head),
        invested: triInvested(head),
        profit: triProfit(head),
      };
      continue;
    }

    const items: Investment[] = section.slice(1).map((r) => ({
      ticker: (r[0] ?? '').trim(),
      name: (r[1] ?? '').trim(),
      location: (r[2] ?? '').trim(),
      liquid: (r[3] ?? '').toLowerCase().trim() === 'yes',
      unitValue: parseAmount(r[4]),
      position: triPosition(r),
      value: triValue(r),
      invested: triInvested(r),
      profit: triProfit(r),
    }));

    categories.push({
      name: headName,
      value: triValue(head),
      invested: triInvested(head),
      profit: triProfit(head),
      items,
    });
  }

  return { categories, totals };
}

function parseDebts(values: string[][]): DebtsData {
  // Columns:
  //   0: name
  //   1: location (lender / institution)
  //   2: balance (household)  3: balance (dakota)  4: balance (natalia)
  //   5: minimum monthly payment (household)
  //   6: minimum monthly to principal (household)
  //   7: final payment (free-form: date, "Paid Off", "TBD", or blank for category headers)
  //   8: interest rate (string %)
  //   9: monthly interest (household)
  //  10: yearly interest (household)
  // Layout: banner header row, blank, then category sections separated by blank
  // rows. Each section starts with a header row holding the category totals,
  // followed by item rows. Final section is a single "Total Debts" row.
  const triBalance = (row: string[] | undefined): Tri => ({
    household: parseAmount(row?.[2]),
    dakota: parseAmount(row?.[3]),
    natalia: parseAmount(row?.[4]),
  });

  const sections: string[][][] = [];
  let current: string[][] = [];
  // Skip the banner row.
  for (const r of values.slice(1)) {
    if ((r[0] ?? '').trim() === '') {
      if (current.length) sections.push(current);
      current = [];
    } else {
      current.push(r);
    }
  }
  if (current.length) sections.push(current);

  const categories: DebtCategory[] = [];
  let totals: DebtsData['totals'] = {
    balance: { household: 0, dakota: 0, natalia: 0 },
    minimumPayment: 0,
    minimumPrincipal: 0,
    monthlyInterest: 0,
    yearlyInterest: 0,
  };

  for (const section of sections) {
    const head = section[0];
    const headName = (head?.[0] ?? '').trim();

    if (headName.toLowerCase() === 'total debts') {
      totals = {
        balance: triBalance(head),
        minimumPayment: parseAmount(head?.[5]),
        minimumPrincipal: parseAmount(head?.[6]),
        monthlyInterest: parseAmount(head?.[9]),
        yearlyInterest: parseAmount(head?.[10]),
      };
      continue;
    }

    const items: DebtItem[] = section.slice(1).map((r) => ({
      name: (r[0] ?? '').trim(),
      location: (r[1] ?? '').trim(),
      balance: triBalance(r),
      minimumPayment: parseAmount(r[5]),
      minimumPrincipal: parseAmount(r[6]),
      finalPayment: (r[7] ?? '').trim(),
      interestRate: parsePercent(r[8]) / 100,
      monthlyInterest: parseAmount(r[9]),
      yearlyInterest: parseAmount(r[10]),
    }));

    categories.push({
      name: headName,
      balance: triBalance(head),
      minimumPayment: parseAmount(head?.[5]),
      minimumPrincipal: parseAmount(head?.[6]),
      monthlyInterest: parseAmount(head?.[9]),
      yearlyInterest: parseAmount(head?.[10]),
      items,
    });
  }

  return { categories, totals };
}

function parseRetirement(values: string[][]): RetirementProjection {
  // Header layout (rows 0-3, then a blank row, then year sections):
  //   Row 0: title + assumption labels/values interspersed across columns
  //   Row 1: person name (Dakota / Natalia) — ignored
  //   Row 2: column headers
  //   Row 3: sub-headers (Monthly/Annual splits)
  //   Row 4: blank
  //
  // Year sections are blocks separated by a blank row. The first row of a
  // section carries Year + Age + first account; subsequent rows have only
  // the account name + numbers; the row whose Account column equals "Total"
  // is the year-totals row.
  //
  // Columns of interest (per individual account / total row):
  //    0: year (only on first row of section)
  //    1: age (only on first row of section)
  //    2: account name (or "Total")
  //    3: initial amount
  //    7: personal contribution (annual)
  //   10: match (annual)
  //   12: total contribution (annual)
  //   15: average return (annual $)
  //   16: withdrawals
  //   17: final amount
  //   18: portfolio percentage
  //   19: withdrawal-rule annual amount (sheet computes this with the 4% rule)
  const headerRow = values[0] ?? [];
  // Assumptions live as label/value pairs at fixed offsets in the header row.
  const hysaApy = parsePercent(headerRow[5]) / 100;
  const averageReturnRate = parsePercent(headerRow[8]) / 100;
  const withdrawalRule = parsePercent(headerRow[11]) / 100;

  // Group data rows (skip the four header rows) into year-sections by blank
  // separators.
  const sections: string[][][] = [];
  let current: string[][] = [];
  for (const r of values.slice(4)) {
    if ((r[2] ?? '').trim() === '' && (r[0] ?? '').trim() === '') {
      if (current.length) sections.push(current);
      current = [];
    } else {
      current.push(r);
    }
  }
  if (current.length) sections.push(current);

  const years: RetirementYear[] = [];
  for (const section of sections) {
    if (section.length === 0) continue;
    const first = section[0];
    const year = parseInt((first[0] ?? '').trim(), 10);
    const age = parseInt((first[1] ?? '').trim(), 10);
    if (Number.isNaN(year)) continue;

    const accounts: RetirementAccount[] = [];
    let totalsRow: string[] | undefined;
    for (const r of section) {
      const accountName = (r[2] ?? '').trim();
      if (!accountName) continue;
      if (accountName.toLowerCase() === 'total') {
        totalsRow = r;
        continue;
      }
      accounts.push({
        name: accountName,
        initial: parseAmount(r[3]),
        contributionAnnual: parseAmount(r[7]),
        matchAnnual: parseAmount(r[10]),
        totalContributionAnnual: parseAmount(r[12]),
        averageReturn: parseAmount(r[15]),
        withdrawals: parseAmount(r[16]),
        finalAmount: parseAmount(r[17]),
        portfolioPercent: parsePercent(r[18]) / 100,
        withdrawalRule: parseAmount(r[19]),
      });
    }

    // Sum from accounts when the Total row is missing or to keep totals in
    // sync with the displayed account list.
    const sumField = (key: keyof RetirementAccount): number =>
      accounts.reduce((s, a) => s + ((a[key] as number) || 0), 0);

    const totals = totalsRow
      ? {
          initial: parseAmount(totalsRow[3]),
          contributionAnnual: parseAmount(totalsRow[7]),
          matchAnnual: parseAmount(totalsRow[10]),
          totalContributionAnnual: parseAmount(totalsRow[12]),
          averageReturn: parseAmount(totalsRow[15]),
          finalAmount: parseAmount(totalsRow[17]),
          withdrawalRule: parseAmount(totalsRow[19]),
        }
      : {
          initial: sumField('initial'),
          contributionAnnual: sumField('contributionAnnual'),
          matchAnnual: sumField('matchAnnual'),
          totalContributionAnnual: sumField('totalContributionAnnual'),
          averageReturn: sumField('averageReturn'),
          finalAmount: sumField('finalAmount'),
          withdrawalRule: sumField('withdrawalRule'),
        };

    years.push({ year, age, accounts, totals });
  }

  return {
    assumptions: { hysaApy, averageReturnRate, withdrawalRule },
    years,
  };
}

function parseBills(values: string[][]): BillsData {
  // Header-driven column lookup so the sheet can add/reorder optional
  // columns (Responsible, Cash Out Item, etc.) without code churn. Row 0
  // is the header row; blank rows are skipped so users can visually group
  // bills if they like.
  const FREQ_MAP: Record<string, BillFrequency> = {
    monthly: 'monthly',
    month: 'monthly',
    'every month': 'monthly',
    'semi-monthly': 'semi-monthly',
    'semi monthly': 'semi-monthly',
    semimonthly: 'semi-monthly',
    bimonthly: 'semi-monthly',
    yearly: 'yearly',
    annual: 'yearly',
    annually: 'yearly',
    year: 'yearly',
    'every year': 'yearly',
    'semi-annually': 'semi-annually',
    'semi annually': 'semi-annually',
    semiannually: 'semi-annually',
    'semi-annual': 'semi-annually',
    biannually: 'semi-annually',
    biannual: 'semi-annually',
    quarterly: 'quarterly',
    quarter: 'quarterly',
    weekly: 'weekly',
    week: 'weekly',
    'every week': 'weekly',
    biweekly: 'biweekly',
    'bi-weekly': 'biweekly',
    'bi weekly': 'biweekly',
    fortnightly: 'biweekly',
    'every 2 weeks': 'biweekly',
    'every two weeks': 'biweekly',
    '2 weeks': 'biweekly',
    'one-time': 'one-time',
    'one time': 'one-time',
    once: 'one-time',
  };

  const classifyResponsible = (raw: string): Bill['responsible'] => {
    const v = raw.trim().toLowerCase();
    if (v === 'dakota') return 'dakota';
    if (v === 'natalia') return 'natalia';
    // Shared / Both / Joint / blank / anything else all mean "everyone".
    return 'shared';
  };

  // Resolve column indices from the header row by matching against known
  // aliases. Missing columns return -1 so the row-reader can fall back to
  // an empty string / zero without crashing.
  const header = (values[0] ?? []).map((c) => (c ?? '').toString().toLowerCase().trim());
  const findCol = (...aliases: string[]): number => {
    for (const a of aliases) {
      const idx = header.indexOf(a);
      if (idx >= 0) return idx;
    }
    return -1;
  };
  const nameCol = (() => {
    const idx = findCol('name', 'bill', 'bills');
    return idx >= 0 ? idx : 0;
  })();
  const frequencyCol = findCol('frequency');
  const dateCol = findCol('date', 'day', 'due', 'due date');
  const accountCol = findCol('account');
  const responsibleCol = findCol('responsible');
  const cashOutItemCol = findCol('cash out item', 'cash out', 'cashout item', 'item');
  const amountCol = findCol('amount');
  const typeCol = findCol('type', 'kind');

  const cell = (r: string[], col: number): string =>
    col >= 0 ? (r[col] ?? '').toString() : '';

  // Sheet's "Type" column accepts Bill / Income (case-insensitive).
  // Anything missing or unrecognized defaults to 'bill' so rows that
  // existed before the column was added keep working.
  const classifyType = (raw: string): Bill['type'] => {
    const v = raw.trim().toLowerCase();
    if (v === 'income' || v === 'paycheck' || v === 'payday') return 'income';
    return 'bill';
  };

  const items: Bill[] = [];
  for (let i = 1; i < values.length; i++) {
    const r = values[i] ?? [];
    const name = cell(r, nameCol).trim();
    if (!name) continue;
    const freqRaw = cell(r, frequencyCol).trim().toLowerCase();
    const frequency: BillFrequency = FREQ_MAP[freqRaw] ?? 'monthly';
    items.push({
      name,
      frequency,
      dayOrDate: cell(r, dateCol).trim(),
      account: cell(r, accountCol).trim(),
      responsible: classifyResponsible(cell(r, responsibleCol)),
      cashOutItem: cell(r, cashOutItemCol).trim(),
      amount: parseAmount(cell(r, amountCol)),
      type: classifyType(cell(r, typeCol)),
    });
  }
  return { items };
}

function parseGifts(values: string[][]): GiftsData {
  // Tab columns: 0 name, 1 recipient, 2 month, 3 household, 4 dakota, 5 natalia.
  // Row 0 is a banner header; blank rows and any without a name are skipped
  // so users can group gifts visually. Recipient is optional — empty string
  // when blank, displayed alongside the gift name on the cards.
  //
  // Month column is a long ("January") or short ("Jan") month name; the
  // parser is case-insensitive and tolerates a trailing "(...)" annotation
  // (e.g. "January (early)"). N/A / TBD / blank → null, surfaced as the
  // "Unscheduled" group on the page.
  const MONTH_NAMES = [
    'january', 'february', 'march', 'april', 'may', 'june',
    'july', 'august', 'september', 'october', 'november', 'december',
  ];
  const parseMonth = (raw: string | undefined): number | null => {
    if (!raw) return null;
    const cleaned = raw.trim().toLowerCase();
    if (!cleaned || cleaned === 'n/a' || cleaned === 'na' || cleaned === 'tbd' || cleaned === '-' || cleaned === '—') {
      return null;
    }
    const numeric = parseInt(cleaned, 10);
    if (!Number.isNaN(numeric) && numeric >= 1 && numeric <= 12) return numeric;
    for (let i = 0; i < MONTH_NAMES.length; i++) {
      if (cleaned.startsWith(MONTH_NAMES[i].slice(0, 3))) return i + 1;
    }
    return null;
  };

  const items: Gift[] = [];
  for (let i = 1; i < values.length; i++) {
    const r = values[i] ?? [];
    const name = (r[0] ?? '').trim();
    if (!name) continue;
    // Skip the sheet's own totals row (e.g. "Total Gifts") so it doesn't
    // render as a gift card on top of the page-level totals.
    if (/^total\b/i.test(name)) continue;
    items.push({
      name,
      recipient: (r[1] ?? '').trim(),
      month: parseMonth(r[2]),
      household: parseAmount(r[3]),
      dakota: parseAmount(r[4]),
      natalia: parseAmount(r[5]),
    });
  }
  return { items };
}

function parseLinks(values: string[][], hyperlinks: string[][]): Link[] {
  // Three-column tab: 0 name (carries the cell hyperlink), 1 purpose,
  // 2 person ('Household' | 'Dakota' | 'Natalia'). The first row is a
  // header. Rows without a hyperlink are skipped — the page is built around
  // clickable cards, so a row with no link can't be rendered usefully.
  const links: Link[] = [];
  for (let i = 1; i < values.length; i++) {
    const name = (values[i][0] ?? '').trim();
    const purpose = (values[i][1] ?? '').trim();
    const personRaw = (values[i][2] ?? '').trim().toLowerCase();
    const person: Link['person'] =
      personRaw === 'dakota' || personRaw === 'natalia' ? personRaw : 'household';
    const url = (hyperlinks[i]?.[0] ?? '').trim();
    if (!name || !url) continue;
    links.push({ name, purpose, url, person });
  }
  return links;
}

function parseIncomeHistory(values: string[][]): AnnualIncome[] {
  // Layout (per-row index after skipping the leading title/header rows):
  //   col 0: Dakota year   | col 1: Dakota source   | col 2: Dakota amount
  //   col 3: spacer
  //   col 4: Natalia year  | col 5: Natalia source  | col 6: Natalia amount
  // The first row is a section banner ("Dakota Income by Year" etc.) and the
  // second row is the column header ("Year | Income Sources | Annual Income").
  const dataRows = values.slice(2).filter((r) => (r[0] ?? '').trim() !== '' || (r[4] ?? '').trim() !== '');

  const byYear = new Map<number, AnnualIncome>();

  for (const r of dataRows) {
    const dakotaYear = parseInt((r[0] ?? '').trim(), 10);
    const nataliaYear = parseInt((r[4] ?? '').trim(), 10);

    const dakotaAmount = parseAmount(r[2]);
    const nataliaAmount = parseAmount(r[6]);
    const dakotaSource = (r[1] ?? '').trim() || undefined;
    const nataliaSource = (r[5] ?? '').trim() || undefined;

    if (!Number.isNaN(dakotaYear)) {
      const existing = byYear.get(dakotaYear) ?? {
        year: dakotaYear,
        dakota: 0,
        natalia: 0,
        household: 0,
      };
      existing.dakota = dakotaAmount;
      existing.dakotaSource = dakotaSource;
      existing.household = existing.dakota + existing.natalia;
      byYear.set(dakotaYear, existing);
    }
    if (!Number.isNaN(nataliaYear)) {
      const existing = byYear.get(nataliaYear) ?? {
        year: nataliaYear,
        dakota: 0,
        natalia: 0,
        household: 0,
      };
      existing.natalia = nataliaAmount;
      existing.nataliaSource = nataliaSource;
      existing.household = existing.dakota + existing.natalia;
      byYear.set(nataliaYear, existing);
    }
  }

  return Array.from(byYear.values()).sort((a, b) => a.year - b.year);
}

// Income by Job tab layout (after row 0 banner + row 1 column header):
//   Dakota  → cols 0-5: Start | End | Company | Title | Monthly | Annual
//   spacer  → col 6
//   Natalia → cols 7-12: same six columns
// Dates are formatted "MMM YYYY" ("Sep 2012"); a job that's still current
// uses "Present" in the End column — we surface that as a null endMonth so
// the chart can anchor it to today.
function parseIncomeByJob(values: string[][]): IncomeByJobData {
  const MONTH_INDEX: Record<string, number> = {
    jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6,
    jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
  };
  // "Sep 2012" → "2012-09"; "Present" / blank → null. Tolerates extra
  // whitespace and case variants since the sheet is hand-edited.
  const toMonth = (raw: string | undefined): string | null => {
    const cell = (raw ?? '').trim();
    if (!cell || /^present$/i.test(cell)) return null;
    const m = cell.match(/^([A-Za-z]{3,9})\s+(\d{4})$/);
    if (!m) return null;
    const month = MONTH_INDEX[m[1].slice(0, 3).toLowerCase()];
    if (!month) return null;
    return `${m[2]}-${String(month).padStart(2, '0')}`;
  };

  const rowFor = (
    person: JobRecord['person'],
    cells: string[],
    base: number
  ): JobRecord | null => {
    const startMonth = toMonth(cells[base]);
    const company = (cells[base + 2] ?? '').trim();
    const title = (cells[base + 3] ?? '').trim();
    // A row only counts if we can place it on the timeline AND it has
    // enough identity to render meaningfully. Title-only or company-only
    // is fine — but a row without a start date is dropped.
    if (!startMonth || (!company && !title)) return null;
    // "Present" → null. If both start and end parse but end < start,
    // we still keep the row; the chart will floor duration at 1 month.
    const endRaw = (cells[base + 1] ?? '').trim();
    const endMonth = /^present$/i.test(endRaw) ? null : toMonth(endRaw);
    return {
      person,
      startMonth,
      endMonth,
      company,
      title,
      monthlySalary: parseAmount(cells[base + 4]),
      annualSalary: parseAmount(cells[base + 5]),
    };
  };

  const dakota: JobRecord[] = [];
  const natalia: JobRecord[] = [];
  // Skip rows 0 (banner) and 1 (column headers); everything after is data.
  for (let i = 2; i < values.length; i++) {
    const r = values[i] ?? [];
    const d = rowFor('dakota', r, 0);
    if (d) dakota.push(d);
    const n = rowFor('natalia', r, 7);
    if (n) natalia.push(n);
  }
  const byStart = (a: JobRecord, b: JobRecord) =>
    a.startMonth.localeCompare(b.startMonth);
  return { dakota: dakota.sort(byStart), natalia: natalia.sort(byStart) };
}

function parseSocialSecurity(values: string[][]): SocialSecurityData {
  // Tab layout (4 cols wide, Tri-style: col 0 label, col 1 household,
  // col 2 dakota, col 3 natalia):
  //   row 0: header banner
  //   "Monthly" section banner row, then 3 rows ("Collect at 62/67/70")
  //   blank row
  //   "Yearly" section banner row, then 3 rows ("Collect at 62/67/70")
  //
  // Rows are matched by label rather than absolute index so the parser
  // tolerates extra blank rows or future reordering. Missing claim-age
  // rows fall back to a zero Tri so older / partial sheets don't crash.
  const triFrom = (row: string[] | undefined): Tri => ({
    household: parseAmount(row?.[1]),
    dakota: parseAmount(row?.[2]),
    natalia: parseAmount(row?.[3]),
  });
  const ZERO: Tri = { household: 0, dakota: 0, natalia: 0 };

  const sectionStart = (label: string): number =>
    values.findIndex((r) => (r[0] ?? '').trim().toLowerCase() === label);

  const claimAgeRow = (
    sectionIdx: number,
    age: ClaimAge
  ): string[] | undefined => {
    if (sectionIdx < 0) return undefined;
    // Look forward up to 5 rows for "Collect at <age>" — tolerates blank
    // rows between the section banner and its rows.
    for (let i = sectionIdx + 1; i < Math.min(values.length, sectionIdx + 6); i++) {
      const label = (values[i]?.[0] ?? '').trim().toLowerCase();
      if (label.includes(String(age))) return values[i];
    }
    return undefined;
  };

  const monthlyIdx = sectionStart('monthly');
  const yearlyIdx = sectionStart('yearly');

  const buildSection = (idx: number): Record<ClaimAge, Tri> => ({
    62: idx >= 0 ? triFrom(claimAgeRow(idx, 62)) : ZERO,
    67: idx >= 0 ? triFrom(claimAgeRow(idx, 67)) : ZERO,
    70: idx >= 0 ? triFrom(claimAgeRow(idx, 70)) : ZERO,
  });

  return {
    monthly: buildSection(monthlyIdx),
    yearly: buildSection(yearlyIdx),
  };
}

export class SheetDataSource implements DataSource {
  constructor(private readonly config: SheetDataSourceConfig) {}

  private async fetchSheet(
    tab: string,
    mode: 'plain' | 'links' = 'plain'
  ): Promise<SheetResponse> {
    const token = this.config.getToken();
    const suffix = mode === 'links' ? '&links=1' : '';
    const url = `${this.config.endpoint}?tab=${encodeURIComponent(tab)}${suffix}`;
    const res = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (res.status === 401) {
      this.config.onAuthError?.();
      throw new Error(`Sheet fetch failed for "${tab}" (401): unauthorized`);
    }
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new Error(`Sheet fetch failed for "${tab}" (${res.status}): ${body.slice(0, 200)}`);
    }
    return (await res.json()) as SheetResponse;
  }

  // Batch fetch — sends one request that collapses every requested tab into
  // a single Sheets API call (values:batchGet). Folds the ~13 plain dashboard
  // reads into 1, keeping us well under the 60 reads/min/user quota even when
  // the page is loaded fresh on multiple devices.
  //
  // fmt / links tabs aren't batchable (they need gridData) and stay on the
  // single-tab path.
  private async fetchBatchValues(tabs: string[]): Promise<Record<string, string[][]>> {
    if (tabs.length === 0) return {};
    const token = this.config.getToken();
    const url = `${this.config.endpoint}?tabs=${encodeURIComponent(tabs.join(','))}`;
    const res = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (res.status === 401) {
      this.config.onAuthError?.();
      throw new Error(`Sheet batch fetch failed (401): unauthorized`);
    }
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new Error(`Sheet batch fetch failed (${res.status}): ${body.slice(0, 200)}`);
    }
    const data = (await res.json()) as { batches?: Record<string, string[][]> };
    return data.batches ?? {};
  }

  // Just the Bills tab — the kiosk's whole slice of the sheet. A kiosk device
  // token is only allowed this tab, so the kiosk can't call load().
  async loadBills(): Promise<BillsData> {
    const batch = await this.fetchBatchValues([this.config.tabs.bills]);
    return parseBills(batch[this.config.tabs.bills] ?? []);
  }

  async load(): Promise<DashboardData> {
    // Every plain-values tab goes through one batchGet; the links tab stays
    // on the single-tab path since it needs hyperlinks (gridData), which
    // the Sheets API can't batch.
    const plainTabs = [
      this.config.tabs.spending,
      this.config.tabs.cashIn,
      this.config.tabs.cashOut,
      this.config.tabs.netWorth,
      this.config.tabs.incomeHistory,
      this.config.tabs.incomeByJob,
      this.config.tabs.assets,
      this.config.tabs.stocksCrypto,
      this.config.tabs.debts,
      this.config.tabs.bills,
      this.config.tabs.gifts,
      this.config.tabs.retirementDakota,
      this.config.tabs.retirementNatalia,
      this.config.tabs.socialSecurity,
    ];

    const [batch, linksResponse] = await Promise.all([
      this.fetchBatchValues(plainTabs),
      this.fetchSheet(this.config.tabs.links, 'links'),
    ]);

    const pick = (tab: string): string[][] => batch[tab] ?? [];

    return {
      spending: parseSpending(pick(this.config.tabs.spending)),
      income: parseCashIn(pick(this.config.tabs.cashIn)),
      netWorth: parseNetWorth(pick(this.config.tabs.netWorth)),
      incomeHistory: parseIncomeHistory(pick(this.config.tabs.incomeHistory)),
      jobs: parseIncomeByJob(pick(this.config.tabs.incomeByJob)),
      budget: parseCashOut(pick(this.config.tabs.cashOut)),
      assets: parseAssets(pick(this.config.tabs.assets)),
      investments: parseStocksCrypto(pick(this.config.tabs.stocksCrypto)),
      debts: parseDebts(pick(this.config.tabs.debts)),
      links: parseLinks(
        linksResponse.values ?? [],
        linksResponse.hyperlinks ?? []
      ),
      bills: parseBills(pick(this.config.tabs.bills)),
      gifts: parseGifts(pick(this.config.tabs.gifts)),
      retirement: {
        dakota: parseRetirement(pick(this.config.tabs.retirementDakota)),
        natalia: parseRetirement(pick(this.config.tabs.retirementNatalia)),
      },
      socialSecurity: parseSocialSecurity(pick(this.config.tabs.socialSecurity)),
    };
  }
}
